import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NavigationStart, Router, RouterLink } from "@angular/router";

import modal from "@uswds/uswds/js/usa-modal";

import { SearchResultType, SearchType } from "@common/plants.enums";

import { NoxiousInvasiveSearchCriteria } from "@models/NoxiousInvasiveSearchCriteria";
import { Plant } from "@models/Plant";
import { PlantInvasiveStatus } from "@models/PlantInvasiveStatus";
import { PlantLegalStatus } from "@models/PlantLegalStatus";
import { PlantNoxiousStatus } from "@models/PlantNoxiousStatus";
import { PlantReference } from "@models/PlantReference";
import { RaritySearchCriteria } from "@models/RaritySearchCriteria";
import { SelectedFilter } from "@models/SelectedFilter";
import { TopLevelSearchResult } from "@models/TopLevelSearchResult";

import { FetchService } from "@services/fetch.service";
import { PlantsService } from "@services/plants.service";
import { SearchService } from "@services/search.service";

import { DownloadModalComponent } from "@shared/download-modal/download-modal.component";
import { PlantsPaginationComponent } from "@shared/plants-pagination/plants-pagination.component";
import { PATHS } from "@shared/resources/paths.constants";

import { AppConfig } from "../app.config";

@Component({
  selector: "search-results",
  imports: [FormsModule, RouterLink, DownloadModalComponent, PlantsPaginationComponent],
  templateUrl: "search-results.component.html",
  styleUrls: ["search-results.component.scss"]
})
export class SearchResultsComponent implements OnInit {
  page = 1;
  numTotalResults: number;
  numTotalImageCount: number;
  totalPages: number;
  maxSize = 5;
  results: Plant[] = [];

  numSynonymResults: number;
  numAcceptedResults: number;
  startResultNumber: number;
  endResultNumber: number;
  downloadLink: string;
  downloadResult: Blob;
  selectedFilters: SelectedFilter[] = [];
  viewBy: string;
  private _fileShare: string = AppConfig.settings.serviceUrls.imageLibraryUrl;
  sources: PlantReference[];

  showResults = true;
  isShowingResults: boolean;
  isDownloadingResults = false;
  isError = false;
  hasFetchFailed = false;

  selectedRarityCriteria: RaritySearchCriteria;
  selectedNoxiousInvasiveCriteria: NoxiousInvasiveSearchCriteria;
  globalURL = "";
  natureServeURL = "https://www.natureserve.org/";
  conservationStatusURL = "https://www.natureserve.org/conservation-status-assessment";
  featureFlags: { [key: string]: boolean } = {};
  plantsHelpDocUrl: string;

  constructor(
    private appConfig: AppConfig,
    private fetchService: FetchService,
    public searchService: SearchService,
    private plantsService: PlantsService,
    private router: Router
  ) {
    this.featureFlags = this.appConfig.getFeatureFlags();
    this.plantsHelpDocUrl =
      this.appConfig.getPlantsUrl() + PATHS.PLANTS_HELP_DOC_WETLAND_STATS_INDICATOR;
    searchService.searchResultsUpdated$.subscribe(this.updateResults.bind(this));
    plantsService.serviceCallFailed$.subscribe(this.updateErrorStatus.bind(this));
    this.fetchService.serviceCallFailed$.subscribe(() => (this.hasFetchFailed = true));
    searchService.searchType = this.router.url.startsWith("/stateSearch")
      ? SearchType.StateSearch
      : this.router.url.startsWith("/group-search")
        ? SearchType.GroupSearch
        : this.router.url.startsWith("/duration-search")
          ? SearchType.DurationSearch
          : this.router.url.startsWith("/growth-habit-search")
            ? SearchType.GrowthHabitSearch
            : this.router.url.startsWith("/rarity-search")
              ? SearchType.RaritySearch
              : this.router.url.startsWith("/wetland-search")
                ? SearchType.WetlandSearch
                : this.router.url.startsWith("/noxious-invasive-search")
                  ? SearchType.NoxiousInvasiveSearch
                  : this.router.url.startsWith("/fact-sheet-plant-guide-search")
                    ? SearchType.FactSheetPlantGuideSearch
                    : this.router.url.startsWith("/characteristics-search")
                      ? SearchType.CharacteristicsSearch
                      : this.router.url.startsWith("/image-search")
                        ? SearchType.ImageSearch
                        : SearchType.BasicSearch;

    this.router.events.forEach((event) => {
      if (event instanceof NavigationStart) {
        searchService.searchType = this.router.url.startsWith("/state-search")
          ? SearchType.StateSearch
          : this.router.url.startsWith("/group-search")
            ? SearchType.GroupSearch
            : this.router.url.startsWith("/duration-search")
              ? SearchType.DurationSearch
              : this.router.url.startsWith("/growth-habit-search")
                ? SearchType.GrowthHabitSearch
                : this.router.url.startsWith("/rarity-search")
                  ? SearchType.RaritySearch
                  : this.router.url.startsWith("/wetland-search")
                    ? SearchType.WetlandSearch
                    : this.router.url.startsWith("/noxious-invasive-search")
                      ? SearchType.NoxiousInvasiveSearch
                      : this.router.url.startsWith("/fact-sheet-plant-guide-search")
                        ? SearchType.FactSheetPlantGuideSearch
                        : this.router.url.startsWith("/characteristics-search")
                          ? SearchType.CharacteristicsSearch
                          : this.router.url.startsWith("/image-search")
                            ? SearchType.ImageSearch
                            : SearchType.BasicSearch;
      }
    });
  }

  ngOnInit(): void {
    this.viewBy = this.searchService.viewBy;
    if (this.searchService.searchType == SearchType.CharacteristicsSearch) {
      this.showResults = true;
    }
    this.updateResults();
  }

  clearAllSelectedFilters(): void {
    if (this.searchService.searchCriteria) {
      this.searchService.searchCriteria.FilterOptions.forEach((o) =>
        o.Filters.forEach((f) => (f.IsSelected = false))
      );
      this.searchService.searchCriteria.Offset = -1;

      this.searchService.updateSearchStatus(true);

      if (this.searchService.searchType == SearchType.StateSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.State
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.GroupSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Group
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.DurationSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Duration
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.GrowthHabitSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.GrowthHabit
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.RaritySearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Rarity
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.WetlandSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Wetland
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.NoxiousInvasiveSearch) {
        const isInvasiveSearch = this.searchService.searchCriteria.InvasiveLocations.length > 0;
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: isInvasiveSearch ? SearchResultType.Invasive : SearchResultType.Noxious
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.FactSheetPlantGuideSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.FactSheetPlantGuide
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.CharacteristicsSearch) {
        this.plantsService
          .getPlantsWithCharacteristics(this.searchService.searchCriteria)
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.ImageSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Image
          })
          .then(this.updateFilteredResults.bind(this));
      } else {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Basic
          })
          .then(this.updateFilteredResults.bind(this));
      }
    }
  }

  removeSelectedFilter(selectedFilter: SelectedFilter): void {
    if (this.searchService.searchCriteria) {
      this.searchService.searchCriteria.FilterOptions.find(
        (o) => o.Name == selectedFilter.FilterName
      ).Filters.find((f) => f.Id == selectedFilter.FilterValueId).IsSelected = false;
      this.searchService.searchCriteria.Offset = -1;

      this.searchService.updateSearchStatus(true);

      if (this.searchService.searchType == SearchType.StateSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.State
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.GroupSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Group
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.DurationSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Duration
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.GrowthHabitSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.GrowthHabit
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.RaritySearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Rarity
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.WetlandSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Wetland
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.NoxiousInvasiveSearch) {
        const isInvasiveSearch = this.searchService.searchCriteria.InvasiveLocations.length > 0;
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: isInvasiveSearch ? SearchResultType.Invasive : SearchResultType.Noxious
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.FactSheetPlantGuideSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.FactSheetPlantGuide
          })
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.CharacteristicsSearch) {
        this.plantsService
          .getPlantsWithCharacteristics(this.searchService.searchCriteria)
          .then(this.updateFilteredResults.bind(this));
      } else if (this.searchService.searchType == SearchType.ImageSearch) {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Image
          })
          .then(this.updateFilteredResults.bind(this));
      } else {
        this.fetchService
          .fetchPlantsSearchResults({
            ...this.searchService.searchCriteria,
            Type: SearchResultType.Basic
          })
          .then(this.updateFilteredResults.bind(this));
      }
    }
  }

  displayCharacteristicsResults() {
    this.showResults = true;
  }

  updateErrorStatus() {
    this.isError = true;
    this.selectedFilters = [];
    this.results = [];
    this.searchService.updateSearchStatus(false);
  }

  handlePageChange(page: number): void {
    this.page = page;
    this.calculatePageRange();
    this.changePage(this.page);
  }

  calculatePageRange(): void {
    const range = 1;

    this.searchService.startPage = Math.max(1, this.page - range);
    this.searchService.endPage = Math.min(this.totalPages, this.page + range);

    // Adjust the start page if we're close to the end of the page range
    if (this.page + range > this.totalPages) {
      this.searchService.startPage = Math.max(1, this.totalPages - range * 2);
    }

    // Adjust the end page if we're close to the start of the page range
    if (this.page - range < 1) {
      this.searchService.endPage = Math.min(this.totalPages, range * 2 + 1);
    }
  }

  changePage(page: number) {
    this.page = page;
    this.searchService.searchPage = page;
    const searchCriteria = this.searchService.searchCriteria;
    searchCriteria.Offset = (this.searchService.searchPage - 1) * 25;
    this.searchService.searchCriteria = searchCriteria;

    this.searchService.updateSearchStatus(true);
    this.searchService.viewBy = this.viewBy;
    if (this.searchService.searchType == SearchType.StateSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.State
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.GroupSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Group
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.DurationSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Duration
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.GrowthHabitSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.GrowthHabit
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.RaritySearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Rarity
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.WetlandSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Wetland
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.NoxiousInvasiveSearch) {
      const isInvasiveSearch = this.searchService.searchCriteria.InvasiveLocations.length > 0;
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: isInvasiveSearch ? SearchResultType.Invasive : SearchResultType.Noxious
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.FactSheetPlantGuideSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.FactSheetPlantGuide
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.CharacteristicsSearch) {
      this.plantsService
        .getPlantsWithCharacteristics(searchCriteria)
        .then(this.displayResults.bind(this));
    } else if (this.searchService.searchType == SearchType.ImageSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Image
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    } else {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Basic
          },
          this.page
        )
        .then(this.displayResults.bind(this));
    }
  }

  displayResults(result: TopLevelSearchResult): void {
    const currentUrl = this.router.url.split("?")[0];
    if (currentUrl != this.router.url) {
      this.router
        .navigate([currentUrl], { queryParams: { resultId: result.ResultId } })
        .then(() => {
          this.searchService.updateSearchStatus(false);
          result.TotalResults = this.searchService.result.TotalResults;
          result.FilterOptions = this.searchService.result.FilterOptions;
          result.UnfilteredPlantIds = this.searchService.result.UnfilteredPlantIds;

          this.searchService.result = result;

          result.SynonymResults = this.searchService.result.SynonymResults;
          result.AcceptedResults = this.searchService.result.AcceptedResults;

          this.startResultNumber = (this.searchService.searchPage - 1) * 25 + 1;
          this.endResultNumber =
            this.numTotalResults < this.startResultNumber + 25
              ? this.numTotalResults
              : this.startResultNumber + 25 - 1;
          this.results = result.PlantResults;
          this.sources = result.Sources;

          if (!this.searchService.criteriaHistory.has(result.ResultId))
            this.searchService.criteriaHistory.set(
              result.ResultId,
              this.searchService.searchCriteria
            );

          if (!this.searchService.searchTypeHistory.has(result.ResultId))
            this.searchService.searchTypeHistory.set(
              result.ResultId,
              this.searchService.searchType
            );

          if (
            this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length ==
            0
          )
            this.searchService.resultHistory.push(result);
        });
      this.calculatePageRange();
    }
  }

  updateResults() {
    this.isError = false;

    // Sort by scientific name by default
    if (this.searchService.result) {
      this.numTotalResults = this.searchService.result.TotalResults;
      this.numTotalImageCount = this.searchService.result.TotalImageCount;
      this.numSynonymResults = this.searchService.result.SynonymResults;
      this.numAcceptedResults = this.searchService.result.AcceptedResults;
      this.sources = this.searchService.result.Sources;
      this.totalPages = Math.ceil(this.numTotalResults / 25);

      if (this.searchService.result.Offset) {
        this.page =
          this.searchService.result.Offset < 0
            ? 1
            : Math.ceil(this.searchService.result.Offset / 25) + 1;
        this.searchService.searchPage = this.page;
        this.startResultNumber = (this.searchService.searchPage - 1) * 25 + 1;
        this.endResultNumber =
          this.numTotalResults < this.startResultNumber + 25
            ? this.numTotalResults
            : this.startResultNumber + 25 - 1;
      } else {
        this.page = 1;
        this.searchService.searchPage = 1;
        this.startResultNumber = 1;
        this.endResultNumber =
          this.numTotalResults < this.startResultNumber + 25
            ? this.numTotalResults
            : this.startResultNumber + 25 - 1;
      }

      this.selectedFilters = [];
      if (this.searchService.result.FilterOptions)
        this.searchService.result.FilterOptions.forEach(
          (o) =>
            (this.selectedFilters = this.selectedFilters.concat(
              o.Filters.filter((f) => f.IsSelected).map(
                (f) => new SelectedFilter(o.Name, f.Id, f.Display)
              )
            ))
        );
      this.results = this.searchService.result.PlantResults;
    }
    if (this.sources !== null && this.sources !== undefined) {
      if (
        this.sources.filter(
          (x) => x.PublicationPlace === "Arlington, VA" && x.Title.includes("NatureServe")
        )[0] !== undefined
      ) {
        this.globalURL = this.sources.filter(
          (x) => x.PublicationPlace === "Arlington, VA" && x.Title.includes("NatureServe")
        )[0].PublicationURL;
      }
    }
  }

  sortResults(sortBy: string): void {
    const searchCriteria = this.searchService.searchCriteria;
    searchCriteria.Offset = -1;
    searchCriteria.SortBy = sortBy;
    this.searchService.searchCriteria = searchCriteria;

    this.searchService.updateSearchStatus(true);

    if (this.searchService.searchType == SearchType.StateSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.State
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.GroupSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Group
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.DurationSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Duration
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.GrowthHabitSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.GrowthHabit
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.RaritySearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Rarity
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.WetlandSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Wetland
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.NoxiousInvasiveSearch) {
      const isInvasiveSearch = this.searchService.searchCriteria.InvasiveLocations.length > 0;
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: isInvasiveSearch ? SearchResultType.Invasive : SearchResultType.Noxious
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.FactSheetPlantGuideSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.FactSheetPlantGuide
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.CharacteristicsSearch) {
      this.plantsService
        .getPlantsWithCharacteristics(searchCriteria)
        .then(this.updateSortedResults.bind(this));
    } else if (this.searchService.searchType == SearchType.ImageSearch) {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Image
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    } else {
      this.fetchService
        .fetchPlantsSearchResults(
          {
            ...searchCriteria,
            Type: SearchResultType.Basic
          },
          this.page
        )
        .then(this.updateSortedResults.bind(this));
    }
  }

  updateSortedResults(result: TopLevelSearchResult): void {
    const currentUrl = this.router.url.split("?")[0];

    if (currentUrl != this.router.url) {
      this.router
        .navigate([currentUrl], { queryParams: { resultId: result.ResultId } })
        .then(() => {
          this.searchService.updateSearchStatus(false);

          this.searchService.result = result;

          if (!this.searchService.criteriaHistory.has(result.ResultId))
            this.searchService.criteriaHistory.set(
              result.ResultId,
              this.searchService.searchCriteria
            );

          if (!this.searchService.searchTypeHistory.has(result.ResultId))
            this.searchService.searchTypeHistory.set(
              result.ResultId,
              this.searchService.searchType
            );

          if (
            this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length ==
            0
          )
            this.searchService.resultHistory.push(result);
        });
    }
  }

  updateFilteredResults(result: TopLevelSearchResult): void {
    const currentUrl = this.router.url.split("?")[0];

    if (currentUrl != this.router.url) {
      this.router
        .navigate([currentUrl], { queryParams: { resultId: result.ResultId } })
        .then(() => {
          this.searchService.updateSearchStatus(false);

          this.searchService.result = result;

          if (!this.searchService.criteriaHistory.has(result.ResultId))
            this.searchService.criteriaHistory.set(
              result.ResultId,
              this.searchService.searchCriteria
            );

          if (!this.searchService.searchTypeHistory.has(result.ResultId))
            this.searchService.searchTypeHistory.set(
              result.ResultId,
              this.searchService.searchType
            );

          if (
            this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length ==
            0
          )
            this.searchService.resultHistory.push(result);
        });
    }
  }

  getSortedResults(synonyms: Plant[]): Plant[] {
    if (synonyms) {
      return synonyms.sort((a, b) => {
        return a.ScientificName.replace(/<\/i>.*/, "")
          .replace("×", "")
          .localeCompare(b.ScientificName.replace(/<\/i>.*/, "").replace("×", ""));
      });
    }
  }

  getPlantImageUrl(profileImageFilename: string): string {
    if (profileImageFilename == null || typeof profileImageFilename == "undefined") {
      return null;
    }
    return this._fileShare + "/thumbnail/" + profileImageFilename;
  }

  downloadEntireSet(): void {
    const searchCriteria = this.searchService.searchCriteria;
    searchCriteria.Offset = null;

    const keyboardEvent = new KeyboardEvent("click");
    modal.toggleModal(keyboardEvent);

    this.isDownloadingResults = true;

    if (this.searchService.searchType == SearchType.StateSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.State
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.GroupSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.Group
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.DurationSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.Duration
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.GrowthHabitSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.GrowthHabit
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.RaritySearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.Rarity
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.WetlandSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.Wetland
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.NoxiousInvasiveSearch) {
      const isInvasiveSearch = this.searchService.searchCriteria.InvasiveLocations.length > 0;
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: isInvasiveSearch ? SearchResultType.Invasive : SearchResultType.Noxious
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.FactSheetPlantGuideSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.FactSheetPlantGuide
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else if (this.searchService.searchType == SearchType.ImageSearch) {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.Image
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    } else {
      this.fetchService
        .fetchPlantsForDownload(
          {
            ...searchCriteria,
            Type: SearchResultType.Basic
          },
          0,
          1
        )
        .then(this.showDownloadWindow.bind(this));
    }
  }

  showDownloadWindow(data: Blob): void {
    this.isDownloadingResults = false;

    this.downloadResult = data;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(window.navigator && (window.navigator as any).msSaveOrOpenBlob)) {
      this.downloadLink = URL.createObjectURL(data);
    } else {
      // For IE compatibility, set to empty string instead of javascript: URL
      this.downloadLink = "";
    }
  }

  startDownload(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window.navigator as any).msSaveOrOpenBlob(this.downloadResult, "SearchResults.csv");
    }
  }

  isReturningSynonyms(): boolean {
    return (
      this.searchService.searchType == SearchType.BasicSearch ||
      this.searchService.searchType == SearchType.StateSearch ||
      this.searchService.searchType == SearchType.GroupSearch ||
      this.searchService.searchType == SearchType.DurationSearch ||
      this.searchService.searchType == SearchType.GrowthHabitSearch ||
      this.searchService.searchType == SearchType.RaritySearch ||
      this.searchService.searchType == SearchType.WetlandSearch ||
      this.searchService.searchType == SearchType.NoxiousInvasiveSearch ||
      this.searchService.searchType == SearchType.FactSheetPlantGuideSearch ||
      this.searchService.searchType == SearchType.CharacteristicsSearch ||
      this.searchService.searchType == SearchType.ImageSearch
    );
  }

  getTotalNumberOfResults(): number {
    return this.numAcceptedResults + this.numSynonymResults;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getWetlandDataDisplay(wetlandData: any): string {
    if (wetlandData.ParentRegion)
      return `${wetlandData.WetlandCode} (${wetlandData.ParentRegion} / ${wetlandData.SubRegion})`;
    else return `${wetlandData.WetlandCode} (${wetlandData.SubRegion})`;
  }

  getRarityStatusDisplay(rarityStatus: PlantLegalStatus): string {
    if (
      rarityStatus &&
      rarityStatus.ProtectedStatuses &&
      rarityStatus.ProtectedStatuses.length > 0
    ) {
      return `${rarityStatus.ProtectedStatuses.join(", ")}`;
    } else return "";
  }

  getNoxiousStatusDisplay(noxiousStatus: PlantNoxiousStatus): string {
    return `${noxiousStatus.NoxiousStatuses.join(", ")}`;
  }

  getInvasiveStatusDisplay(invasiveStatus: PlantInvasiveStatus): string {
    return `${invasiveStatus.InvasiveStatuses.join(", ")}`;
  }

  getDocumentLinkText(url: string) {
    return url.endsWith("pdf") ? "(pdf)" : "(doc)";
  }

  public showRarityByState(state) {
    this.plantsService
      .getPlantsRarityByState(state, this.selectedRarityCriteria)
      .then(this.displayRarityResults.bind(this))
      .catch(this.displayRarityResults.bind(this));
  }

  displayRarityResults(result: TopLevelSearchResult): void {
    this.isShowingResults = true;
    this.router
      .navigate(["/rarity-search/rarity-search-results"], {
        queryParams: { resultId: result.ResultId }
      })
      .then(() => {
        this.searchService.updateSearchStatus(false);

        this.searchService.result = result;
        this.searchService.searchCriteria = result.SearchCriteria;
        if (!this.searchService.criteriaHistory.has(result.ResultId))
          this.searchService.criteriaHistory.set(
            result.ResultId,
            this.searchService.searchCriteria
          );

        if (!this.searchService.searchTypeHistory.has(result.ResultId))
          this.searchService.searchTypeHistory.set(result.ResultId, this.searchService.searchType);

        if (
          this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length == 0
        )
          this.searchService.resultHistory.push(result);
      });
  }

  public showInvasiveByState(state) {
    this.plantsService
      .getPlantsInvasiveByState(state, this.selectedNoxiousInvasiveCriteria)
      .then(this.displayInvasiveResults.bind(this))
      .catch(this.displayInvasiveResults.bind(this));
  }

  displayInvasiveResults(result: TopLevelSearchResult): void {
    this.isShowingResults = true;
    this.router
      .navigate(["/noxious-invasive-search/noxious-invasive-search-results"], {
        queryParams: { resultId: result.ResultId }
      })
      .then(() => {
        this.searchService.updateSearchStatus(false);

        this.searchService.result = result;
        this.searchService.searchCriteria = result.SearchCriteria;

        if (!this.searchService.criteriaHistory.has(result.ResultId))
          this.searchService.criteriaHistory.set(
            result.ResultId,
            this.searchService.searchCriteria
          );

        if (!this.searchService.searchTypeHistory.has(result.ResultId))
          this.searchService.searchTypeHistory.set(result.ResultId, this.searchService.searchType);

        if (
          this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length == 0
        )
          this.searchService.resultHistory.push(result);
      });
  }

  public showNoxiousByState(state) {
    this.plantsService
      .getPlantsNoxiousByState(state, this.selectedNoxiousInvasiveCriteria)
      .then(this.displayNoxiousResults.bind(this))
      .catch(this.displayNoxiousResults.bind(this));
  }

  updateViewBy() {
    this.searchService.viewBy = this.viewBy;
  }

  displayNoxiousResults(result: TopLevelSearchResult): void {
    this.isShowingResults = true;
    this.router
      .navigate(["/noxious-invasive-search/noxious-invasive-search-results"], {
        queryParams: { resultId: result.ResultId }
      })
      .then(() => {
        this.searchService.updateSearchStatus(false);

        this.searchService.result = result;
        this.searchService.searchCriteria = result.SearchCriteria;

        if (!this.searchService.criteriaHistory.has(result.ResultId))
          this.searchService.criteriaHistory.set(
            result.ResultId,
            this.searchService.searchCriteria
          );

        if (!this.searchService.searchTypeHistory.has(result.ResultId))
          this.searchService.searchTypeHistory.set(result.ResultId, this.searchService.searchType);

        if (
          this.searchService.resultHistory.filter((r) => r.ResultId == result.ResultId).length == 0
        )
          this.searchService.resultHistory.push(result);
      });
  }
}
